import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

type Slide =
  | { type: "video"; src: string; poster?: string }
  | { type: "image"; src: string };

const RecentlySoldSection: React.FC = () => {
  const { t } = useTranslation();

  const sliderRef = useRef<HTMLDivElement>(null);

  // --- MEDIA (KEEP EXTENSIONS EXACT) ---
  const portraits = useMemo(
    () => ["/sold/A.JPG", "/sold/B.JPG", "/sold/C.PNG", "/sold/D.PNG"],
    []
  );

  const landscapes = useMemo(
    () => Array.from({ length: 23 }, (_, i) => `/sold/${i + 1}.png`),
    []
  );

  const videoSrc = "/sold/video.MP4";
  const videoPoster = "/sold/video-poster.jpg";

  // --- ONE SLIDER LIST ---
  const slides: Slide[] = useMemo(
    () => [
      { type: "video", src: videoSrc, poster: videoPoster },
      ...portraits.map((src) => ({ type: "image" as const, src })),
      ...landscapes.map((src) => ({ type: "image" as const, src })),
    ],
    [portraits, landscapes]
  );

  // --- LIGHTBOX ---
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openLightbox = useCallback((index: number) => {
    setActiveIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const next = useCallback(() => {
    setActiveIndex((i) => (i + 1) % slides.length);
  }, [slides.length]);

  const prev = useCallback(() => {
    setActiveIndex((i) => (i - 1 + slides.length) % slides.length);
  }, [slides.length]);

  // --- keyboard + lock scroll ---
  useEffect(() => {
    if (!lightboxOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };

    window.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [lightboxOpen, closeLightbox, next, prev]);

  // --- swipe mobile in lightbox ---
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (diff > 60) next();
    if (diff < -60) prev();
  };

  // --- Slider scroll helpers (responsive) ---
  const scrollByCard = (dir: "left" | "right") => {
    if (!sliderRef.current) return;

    // scroll selon la largeur écran (plus smooth que 520 fixe)
    const base = Math.min(window.innerWidth * 0.8, 560);
    const amount = dir === "left" ? -base : base;

    sliderRef.current.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <>
      <section className="relative py-32 sm:py-44 px-4 sm:px-6 bg-cream overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto">
          {/* HEADER */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto mb-16 sm:mb-24"
          >
            <span className="block font-oswald uppercase tracking-[0.45em] text-[#a5694f] text-[10px] mb-6">
              {t("recentlySold.eyebrow")}
            </span>

            <h2 className="font-oswald uppercase tracking-tight text-4xl sm:text-5xl md:text-6xl text-stone-900 leading-[1.05]">
              {t("recentlySold.title")}
            </h2>

            <p className="mt-8 font-playfair italic text-base sm:text-lg text-stone-500/70 leading-relaxed max-w-2xl mx-auto">
              {t("recentlySold.subtitle")}
            </p>
          </motion.div>

          {/* TOP CONTROLS */}
          <div className="flex items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-3">
              <span className="font-oswald uppercase tracking-[0.35em] text-[10px] text-[#a5694f]">
                {t("recentlySold.sliderLabel")}
              </span>
              <span className="hidden sm:inline text-stone-400 text-xs">
                {t("recentlySold.sliderHint")}
              </span>
            </div>

            <div className="hidden sm:flex items-center gap-3">
              <button
                type="button"
                onClick={() => scrollByCard("left")}
                aria-label="Previous"
                className="w-12 h-12 rounded-full bg-white/70 backdrop-blur-md border border-stone-900/10 hover:bg-[#a5694f] hover:text-white transition-all flex items-center justify-center"
              >
                <ChevronLeft size={18} />
              </button>

              <button
                type="button"
                onClick={() => scrollByCard("right")}
                aria-label="Next"
                className="w-12 h-12 rounded-full bg-white/70 backdrop-blur-md border border-stone-900/10 hover:bg-[#a5694f] hover:text-white transition-all flex items-center justify-center"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          {/* SLIDER */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="relative"
          >
            <div
              ref={sliderRef}
              className="flex gap-7 overflow-x-auto snap-x snap-mandatory pb-6 pr-2 sold-slider"
            >
              {slides.map((slide, idx) => (
                <motion.button
                  type="button"
                  key={`${slide.type}-${slide.src}`}
                  whileHover={{ y: -10 }}
                  onClick={() => openLightbox(idx)}
                  className="
                    group relative shrink-0 snap-center
                    w-[88vw] sm:w-[70vw] lg:w-[56vw]
                    rounded-[2.8rem] overflow-hidden
                    bg-stone-100 shadow-[0_45px_120px_-80px_rgba(0,0,0,0.65)]
                    text-left
                  "
                >
                  <div className="relative aspect-[16/10] w-full">
                    {slide.type === "video" ? (
                      <>
                        <video
                          className="absolute inset-0 w-full h-full object-cover scale-[1.03] group-hover:scale-[1.01] transition-transform duration-[1600ms] ease-out"
                          src={slide.src}
                          poster={slide.poster}
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="metadata"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
                        <div className="absolute bottom-6 left-7 right-7">
                          <p className="font-oswald uppercase tracking-[0.35em] text-[10px] text-white/60">
                            {t("recentlySold.videoBadge")}
                          </p>
                          <h3 className="mt-2 font-playfair italic text-2xl sm:text-3xl text-white">
                            {t("recentlySold.videoTitle")}
                          </h3>
                        </div>
                      </>
                    ) : (
                      <>
                        <img
                          src={slide.src}
                          alt={`Sold ${idx + 1}`}
                          className="absolute inset-0 w-full h-full object-cover scale-[1.02] group-hover:scale-[1.08] transition-transform duration-[1600ms] ease-out"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="absolute bottom-6 left-7 right-7 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <p className="font-oswald uppercase tracking-[0.35em] text-[10px] text-white/70">
                            {t("recentlySold.sold")}
                          </p>
                        </div>
                      </>
                    )}

                    <div className="absolute inset-0 ring-1 ring-[#a5694f]/10 group-hover:ring-[#a5694f]/45 transition-all duration-500 rounded-[2.8rem]" />

                    <div className="absolute top-6 right-6 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15">
                      <span className="font-oswald text-[10px] uppercase tracking-[0.35em] text-white/80">
                        {idx + 1}/{slides.length}
                      </span>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            <style>{`
              .sold-slider::-webkit-scrollbar { height: 10px; }
              .sold-slider::-webkit-scrollbar-thumb {
                background: rgba(165,105,79,0.35);
                border-radius: 999px;
              }
              .sold-slider::-webkit-scrollbar-track { background: transparent; }
            `}</style>
          </motion.div>
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] bg-black/95"
          >
            {/* click outside closes */}
            <div className="absolute inset-0 z-0" onClick={closeLightbox} />

            {/* top bar */}
            <div className="absolute top-0 left-0 right-0 px-5 py-5 flex items-center justify-between z-20">
              <div className="text-white/50 font-oswald text-[10px] tracking-[0.35em] uppercase">
                {activeIndex + 1} / {slides.length}
              </div>

              <button
                onClick={closeLightbox}
                type="button"
                aria-label="Close"
                className="w-12 h-12 rounded-full bg-white/10 border border-white/15 text-white flex items-center justify-center hover:bg-[#a5694f] transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* content */}
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              className="relative z-10 w-full h-full flex items-center justify-center px-4 md:px-12"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div className="relative w-full max-w-6xl">
                <div className="w-full h-[72vh] md:h-[82vh] flex items-center justify-center">
                  {slides[activeIndex].type === "video" ? (
                    <video
                      key={slides[activeIndex].src}
                      className="max-h-full max-w-full object-contain rounded-2xl shadow-2xl"
                      src={slides[activeIndex].src}
                      controls
                      autoPlay
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img
                      key={slides[activeIndex].src}
                      src={slides[activeIndex].src}
                      alt={`Sold ${activeIndex + 1}`}
                      className="max-h-full max-w-full object-contain rounded-2xl shadow-2xl"
                      loading="eager"
                      decoding="async"
                    />
                  )}
                </div>

                {/* arrows */}
                {slides.length > 1 && (
                  <>
                    <button
                      onClick={prev}
                      type="button"
                      aria-label="Previous"
                      className="absolute -left-2 md:-left-20 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-[#a5694f] transition-all z-20"
                    >
                      <ChevronLeft size={24} />
                    </button>

                    <button
                      onClick={next}
                      type="button"
                      aria-label="Next"
                      className="absolute -right-2 md:-right-20 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-[#a5694f] transition-all z-20"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default RecentlySoldSection;
